import { Icon } from '@design/components/icon/Icon';
import { SvgIconNames } from '@design/design-tokens/iconography/SvgIcons';
import clsx from 'clsx';
import React, { ButtonHTMLAttributes, ReactNode } from 'react';

import styles from './ButtonLink.module.scss';

type IconPosition = 'left' | 'right';

export interface ButtonLinkProps
  extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Visible link text. */
  label: ReactNode;
  /** Optional icon rendered alongside the label, from the design-system iconography set. */
  iconName?: SvgIconNames;
  /** Icon position relative to the label. Defaults to `'left'`. */
  iconPosition?: IconPosition;
  /** Icon size in pixels. Defaults to `16`. */
  iconSize?: number;
  /** SVG viewBox forwarded to the icon. Override only when the underlying SVG asset uses a non-default viewBox. */
  iconViewBox?: string;
}

/**
 * Link-styled button: a `<button>` that visually reads as a hyperlink
 * (no border or fill, just text in the link color, with an underline
 * on hover). Use for in-app actions that look like links but should
 * not change the URL — for actual navigation, use a real `<a>`/router
 * link. For full-chrome buttons, use `Button`.
 */
export const ButtonLink = ({
  label,
  iconName,
  iconPosition = 'left',
  iconSize = 16,
  iconViewBox,
  className,
  ...rest
}: ButtonLinkProps) => {
  return (
    <button
      type="button"
      data-testid="button-link"
      className={clsx(styles.root, className)}
      {...rest}
    >
      <span
        className={clsx(styles.label, {
          [styles.labelReversed]: iconPosition === 'right',
        })}
        data-button-link-label
      >
        {iconName && (
          <Icon iconName={iconName} size={iconSize} viewBox={iconViewBox} />
        )}
        {label}
      </span>
    </button>
  );
};
